import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Header as TecmaHeader, Drawer } from '@tecma/ds';
import { useTranslation } from '@tecma/i18n';
import type { AvailableLanguage } from '@tecma/ds/lib/components/LanguageSelector/constants/index';

import { ContactUsModal } from 'components/ContactUsModal';
import { authActions } from 'store/sagas/application/auth/auth.actions';
import { getUserFullName } from 'store/slices/auth/selectors';

import './header.scss';


interface HeaderRequiredProps {
  languages: AvailableLanguage[];
  logo: string;
}

// Optional Props
interface HeaderOptionalProps {}

// Combined required and optional props to build the full prop interface
export interface HeaderProps extends HeaderRequiredProps, HeaderOptionalProps {}

const Header: React.FC<HeaderProps> = ({ languages, logo }) => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const [isContactUsModalOpen, setIsContactUsModalOpen] = useState(false);

  const userFullName = useSelector(getUserFullName);

  const handleChangeLanguage = (language: string) => {
    i18n.changeLanguage(language);
  };

  const toggleContactUsModal = () => {
    setIsContactUsModalOpen(!isContactUsModalOpen);
  };

  const handleLogout = () => {
    dispatch(authActions.doLogout());
  };

  return (
    <>
      <TecmaHeader
        logo={logo}
        logoWrapper={LogoWrapper}
        desktopHeaderContent={
          <>
            <TecmaHeader.Item label={userFullName} iconName='user-circle'>
              <TecmaHeader.MenuItem title={userFullName} />
              <TecmaHeader.Divider />
              <TecmaHeader.MenuItem title={t('header.logout')} onClick={handleLogout} />
            </TecmaHeader.Item>
            <TecmaHeader.LanguageSelector currentLanguage={i18n.language} onChangeLanguage={handleChangeLanguage} languages={languages} />
            <TecmaHeader.Item label={t('header.contact-us')} iconName='headset' onClick={toggleContactUsModal} />
          </>
        }
        mobileHeaderContent={<TecmaHeader.Item iconName='headset' onClick={toggleContactUsModal} />}
        mobileDrawerContent={
          <>
            <Drawer.Content>
              {userFullName && (
                <Drawer.Avatar
                  avatarProps={{
                    text: userFullName
                      .match(/(\b\S)?/g)
                      ?.join('')
                      ?.match(/(^\S|\S$)?/g)
                      ?.join('')
                      .toUpperCase(),
                  }}
                  title={userFullName}
                />
              )}

              <div>
                <Drawer.Item label={t('header.languages')} iconName='translate' rightIcon='chevron-right' menuLayout='page'>
                  <Drawer.Languages currentLanguage={i18n.language} onChangeLanguage={handleChangeLanguage} languages={languages} />
                </Drawer.Item>
              </div>
            </Drawer.Content>

            <Drawer.Footer>
              <Drawer.Item label={t('header.logout')} iconName='logout' onClick={handleLogout} />
            </Drawer.Footer>
          </>
        }
      />
      <ContactUsModal isOpen={isContactUsModalOpen} toggleModal={toggleContactUsModal} />
    </>
  );
};

export default React.memo(Header);

const LogoWrapper: React.FC = ({ children }) => <Link to='/'>{children}</Link>;
